import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Grid, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, IconButton } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { defaultDecimalPlaces, defaultNumericPlaces } from 'constants/numeric';
import { withRights } from 'providers/RightsConsumer';
import { Button, TextField } from '@totalsoft/rocket-ui';
import DocumentPartnerExtraInformationWrapper from './DocumentPartnerExtraInformationWrapper';

export const DocumentPartnerExtraInformationDataRowComponent = ({
  section,
  sectionIdx,
  t,
  onPropertyChange,
  clientExtraInformationWrite,
  onAddExtraInfo,
  onDeleteRow,
  rights,
  countryCode,
  onSavePartnerExtraInfos,
  saveDisabled,
  validateExtraInformation,
  savedWithMandatoryPropertiesEmpty,
  saving,
  loadPropertyDetails
}) => {
  const isGridDisplay = section.display === 'Grid';
  const isTextArea = section.textArea === true;

  return (
    <Grid container spacing={2} key={sectionIdx}>
      {isGridDisplay ? (
        <Grid container spacing={2}>
          {section?.values?.map((item, index) => (
            <React.Fragment key={index}>
              {section?.columns?.map((column, columnIdx) =>
                isTextArea ? (
                  <Grid container spacing={2} alignItems='center' key={`${index}_${columnIdx}`}>
                    <Grid item xs={4}>
                      <label
                        htmlFor={`extraInfo_${index}_${column.propertyName}`}
                        style={{ color: 'black', fontSize: '18px', display: 'block', marginLeft: '2rem', marginTop: '2rem' }}
                      >
                        {column.required ? `${column.caption}*` : column.caption}
                      </label>
                    </Grid>
                    <Grid item xs={8}>
                      <TextField
                        id={`extraInfo_${index}_${column.propertyName}`}
                        fullWidth
                        variant='outlined'
                        onChange={e => onPropertyChange(index, column.propertyName, e, sectionIdx)}
                        value={item[column.propertyName]}
                        disabled={rights.readOnlyFull || !clientExtraInformationWrite}
                        required={column.required && !rights.readOnlyFull}
                        error={column.required && !item[column.propertyName] && !rights.readOnlyFull}
                        helperText={column.required && !item[column.propertyName] ? t('Validations.Generic.Mandatory') : ''}
                        InputProps={{
                          inputProps: {
                            decimalScale: column.propertyType === 'INT' ? defaultNumericPlaces : defaultDecimalPlaces
                          }
                        }}
                        sx={{ marginBottom: 1, marginTop: '2rem' }}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid item xs={12} sm={6} md={6} xl={4} key={`${index}_${columnIdx}`} sx={{ padding: '0.5rem' }}>
                    <label
                      htmlFor={`extraInfo_${index}_${column.propertyName}`}
                      style={{ color: 'black', fontSize: '15px', display: 'block', marginLeft: '0.5rem' }}
                    >
                      {column.required ? `${column.caption}*` : column.caption}
                    </label>
                    <DocumentPartnerExtraInformationWrapper
                      column={column}
                      item={item}
                      index={index}
                      onPropertyChange={onPropertyChange}
                      clientExtraInformationWrite={clientExtraInformationWrite}
                      countryCode={countryCode}
                      sectionIdx={sectionIdx}
                      t={t}
                      rights={rights}
                      loadPropertyDetails={loadPropertyDetails}
                    />
                  </Grid>
                )
              )}
            </React.Fragment>
          ))}
        </Grid>
      ) : (
        <Grid item xs={12} sm={12} md={12} sx={{ marginTop: '20px', overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {section?.columns?.map((column, index) => (
                  <Tooltip key={index} title={column.caption}>
                    <TableCell key={index}>{column.required ? `${column.caption}*` : column.caption}</TableCell>
                  </Tooltip>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {section?.values?.map((item, index) => (
                <TableRow key={`${index}`}>
                  {section?.columns?.map((column, columnIdx) => (
                    <TableCell key={`${index}_${columnIdx}`}>
                      <div id={'extra_' + index + '_info'}>
                        <DocumentPartnerExtraInformationWrapper
                          column={column}
                          item={item}
                          index={index}
                          onPropertyChange={onPropertyChange}
                          clientExtraInformationWrite={clientExtraInformationWrite}
                          countryCode={countryCode}
                          sectionIdx={sectionIdx}
                          t={t}
                          rights={rights}
                          loadPropertyDetails={loadPropertyDetails}
                        />
                      </div>
                    </TableCell>
                  ))}
                  <TableCell sx={{ textAlign: 'end' }}>
                    <IconButton
                      id={'extraInfo' + index + '_deleteBtn'}
                      color='primary'
                      variant='text'
                      onClick={() => onDeleteRow(index, sectionIdx)}
                      disabled={rights.readOnlyFull || !clientExtraInformationWrite}
                    >
                      <CancelIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Grid>
      )}
      {!isGridDisplay && (
        <Button
          id={'extraInfo' + 'addBtn'}
          color='primary'
          variant='contained'
          onClick={() => onAddExtraInfo(sectionIdx)}
          disabled={rights.readOnlyFull || !clientExtraInformationWrite}
          sx={{ marginTop: '1.5rem', marginLeft: '1rem' }}
        >
          {t('Add')}
        </Button>
      )}
      <Button
        id={'extraInfo_saveBtn_' + sectionIdx}
        color='primary'
        variant='contained'
        onClick={() => onSavePartnerExtraInfos(sectionIdx)}
        disabled={
          saveDisabled[sectionIdx] ||
          !clientExtraInformationWrite ||
          rights.readOnlyFull ||
          (validateExtraInformation([section]) && !savedWithMandatoryPropertiesEmpty)
        }
        sx={{ marginTop: '1.5rem', marginLeft: '1rem' }}
      >
        {saving[sectionIdx] ? t('Saving') : t('Save')}
      </Button>
    </Grid>
  );
};

DocumentPartnerExtraInformationDataRowComponent.propTypes = {
  section: PropTypes.shape({
    name: PropTypes.string,
    display: PropTypes.string,
    textArea: PropTypes.bool,
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        caption: PropTypes.string.isRequired,
        required: PropTypes.bool.isRequired,
        propertyName: PropTypes.string.isRequired,
        propertyType: PropTypes.string.isRequired,
        propertyCode: PropTypes.string.isRequired
      })
    ),
    values: PropTypes.arrayOf(PropTypes.object)
  }),
  sectionIdx: PropTypes.number,
  t: PropTypes.func,
  onPropertyChange: PropTypes.func,
  clientExtraInformationWrite: PropTypes.bool,
  onAddExtraInfo: PropTypes.func,
  onDeleteRow: PropTypes.func,
  rights: PropTypes.object,
  countryCode: PropTypes.string,
  onSavePartnerExtraInfos: PropTypes.func,
  saveDisabled: PropTypes.array.isRequired,
  validateExtraInformation: PropTypes.func,
  savedWithMandatoryPropertiesEmpty: PropTypes.bool,
  saving: PropTypes.array.isRequired,
  loadPropertyDetails: PropTypes.func
};

export default withTranslation()(withRights(DocumentPartnerExtraInformationDataRowComponent));
